import axios from 'axios';
import { ENV } from 'common/constants';
import { isBrowser } from 'common/constants/config';
import logger from 'common/utils/udp-logger';
import { nanoid } from 'nanoid';

const DOMAIN = {
  [ENV.DEVELOPMENT]: 'http://127.0.0.1:3000',
  [ENV.PRODUCTION]: 'https://www.tencentcloud.com',
  [ENV.DEV]: 'http://11.141.215.222:3000', // portal-main tkex-dev 11.141.215.222
  [ENV.TEST]: 'http://11.168.87.185',
  [ENV.PRE]: 'http://30.184.73.22:3000', // portal-main tkex-pre 30.184.73.22
  [ENV.PROD]: 'http://30.166.95.175:3000', // portal-main tkex-prod(hk1) 30.166.95.175
};

/**
 * @typedef {Object} DomainInfosType
 * @property {String} QCMAIN_HOST 官网域名
 * @property {String} QCCONSOLE_HOST 控制台域名
 * @property {String} QCLOUD_ROOT_HOST cookie域名
 * @property {String} QCBUY_HOST 购买页域名
 * @property {String} QCCONSOLE_CN_HOST 中国站控制台域名
 * @property {String} QCBUY_CN_HOST 中国站购买页域名
 * @property {String} CHINA_HOST 中国站官网域名
 * @author yuiffydong
 */

const BASE_DOMAINS = {
  QCMAIN_HOST: 'www.tencentcloud.com',
  QCLOUD_ROOT_HOST: 'cloud.tencent.com',
  QCCONSOLE_HOST: 'console.tencentcloud.com',
  QCCONSOLE_CN_HOST: 'console.cloud.tencent.com',
  QCBUY_HOST: 'buy.tencentcloud.com',
  QCBUY_CN_HOST: 'buy.cloud.tencent.com',
  CHINA_HOST: 'cloud.tencent.com', // 中国站域名。在国际站域名修改后会和国际站域名、cookie域名区分开，单独处理
};

/**
 * 生成需要的各种相对域名（控制台、购买页等）
 * @param  {String} host 基础host
 * @return {DomainInfosType} 各类域名
 * @author yuiffydong
 */
function getDomains(host) {
  // QCMAIN_HOST 当前域名，如www.tencentcloud.com/intl.cloud.tencent.com/dev.intl.cloud.tencent.com:3001
  const QCMAIN_HOST = host || BASE_DOMAINS.QCMAIN_HOST;
  // QCMAIN_ROOT_HOST 国际站根域名，用于拼接其他域名，不导出。去除首部dev/www和尾部端口号，以便本地调试也能正常使用。如tencentcloud.com/intl.cloud.tencent.com
  const QCMAIN_ROOT_HOST = QCMAIN_HOST.replace(/^(dev\.)?(www\.)?/, '').replace(/:\d+$/, '');
  const QCLOUD_ROOT_HOST = QCMAIN_ROOT_HOST.replace(/^intl\./, ''); // 根域名，用于cookie。如cloud.tencent.com/tencentcloud.com
  const { CHINA_HOST } = BASE_DOMAINS; // 中国站域名，用于跳转
  const QCCONSOLE_HOST = `console.${QCMAIN_ROOT_HOST}`; // 国际控制台域名。新域名是console.tencentcloud.com，旧域名的控制台叫console.intl.cloud.tencent.com
  const QCCONSOLE_CN_HOST = `console.${CHINA_HOST}`; // 中国站控制台域名，用于一些域名替换
  const QCBUY_HOST = `buy.${QCMAIN_ROOT_HOST}`; // 国际购买页域名
  const QCBUY_CN_HOST = `buy.${CHINA_HOST}`; // 中国站购买页域名

  return { QCMAIN_HOST, QCLOUD_ROOT_HOST, QCCONSOLE_HOST, QCCONSOLE_CN_HOST, QCBUY_HOST, QCBUY_CN_HOST, CHINA_HOST };
}
/**
 * 获取各种相对路径域名
 * @returns {DomainInfosType} 各类域名
 * @author yuiffydong
 */
const getDomainInfos = () => {
  let domainInfos = {};
  // console.log('getDomainInfos will judge is browser or not', { proBro: process.browser });
  if (typeof window === 'undefined') {
    // domainInfos = _.get(process, 'domain.req.res.$domains') || {}; // 旧官网是用中间件保存在这，直接获取来用
    // TODO: 后端状态下获取域名
    domainInfos = getDomains(); // 获取默认域名。大仓此处暂时无法获取到当前域名。store.getDomains()会串请求
    // console.log('getDomainInfos on server-side', {
    //   domainInfos,
    // });
  } else if (process && process.browser === true) {
    // domainInfos = window.DOMAIN_LIST || {}; // 旧官网是用中间件保存在这，直接获取来用
    try {
      domainInfos = getDomains(window && window.location.host); // 前端下直接获取当前域名
    } catch (e) {
      logger.info('getDomainInfos get domain from window catch error', e);
    }
  } else {
    logger.info('useDomains can not get domain');
  }
  // console.log('domainInfos', { domainInfos, isClientContext, isServerContext });
  return domainInfos;
};

const getFrontApiBase = () => {
  let baseUrl;
  const domains = getDomainInfos();
  if (domains?.QCMAIN_HOST && isBrowser) {
    // 1. 非流水线(即本地调试) 且 浏览器环境 ---> //intl.cloud.tencent.com (TODO: 直接相对路径即可)
    // 如果是服务端，随便怎么调用都可以？那让多域名只针对浏览器端，否则本地调试会调到测试环境
    baseUrl = `${isBrowser ? '' : 'https:'}//${domains.QCMAIN_HOST}`;
  } else if (process.env.ZONE_ENV) {
    // 2. 流水线 且 buildTime ---> 各流水线环境配置的baseUrl
    if (process.env.npm_lifecycle_event === ENV.BUILD) {
      baseUrl = DOMAIN[process.env.ZONE_ENV];
    }
  } else {
    // 3. 非流水线(即本地调试) 且 服务端环境 ---> baseUrl=http://127.0.0.1:3000
    if (!isBrowser) {
      baseUrl = DOMAIN[ENV.DEVELOPMENT];
    }
  }
  logger.info({
    event: 'getFrontApiBase',
    baseUrl,
    ZONE_ENV: process.env.ZONE_ENV,
    npm_lifecycle_event: process.env.npm_lifecycle_event,
    domains,
    isBrowser,
  });
  return baseUrl || DOMAIN[ENV.PRODUCTION]; // 4. 兜底 ---> baseUrl=https://www.tencentcloud.com
};

const requestInstance = axios.create({
  baseURL: getFrontApiBase() || '/',
  headers: {
    post: { 'Content-Type': 'application/json' },
  },
});

requestInstance.interceptors.request.use(
  (config) => {
    const { headers, baseURL, url, method, params, data } = config;
    const { domain = {} } = process;
    const xReqId = nanoid();
    headers['x-request-id'] = headers?.requestid || domain?.id || xReqId; // 如果 domain.id 不存在，则随机生成一个
    logger.info({
      event: 'geti18n-request',
      headers,
      baseURL,
      url,
      method,
      params,
      data,
    });
    return config;
  },
  (error) => Promise.reject(error),
);

requestInstance.interceptors.response.use(
  (response) => {
    if (response?.status === 200) {
      const {
        config: { headers, baseURL, url, method, params, data },
      } = response;
      logger.info({
        event: 'geti18n-response',
        headers,
        baseURL,
        url,
        method,
        params,
        data,
        resCode: response?.data?.code,
      });
      return response?.data;
    }
    return Promise.reject(response?.data);
  },
  (error) => Promise.reject(error),
);

export default requestInstance;
